/**
 * Site-standard file picker row (same pattern as company Payments tab “Choose file…”):
 * hidden native `<input type="file">`, full-width filename / placeholder, trailing action (default **Select file**)
 * that opens the file picker (distinct from a separate form submit like **Upload** if present).
 */

import { useRef, type ChangeEvent } from 'react'
import { cn } from '@lib/utils/helpers'

export interface IntegratedFileSelectProps {
  /** Current filename, or empty string for placeholder */
  displayName: string
  accept?: string
  disabled?: boolean
  /** Placeholder when `displayName` is empty */
  emptyLabel?: string
  /** Trailing control label (default **Select file**) */
  browseLabel?: string
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
  className?: string
  /** Optional hook for E2E */
  'data-testid'?: string
}

export function IntegratedFileSelect({
  displayName,
  accept,
  disabled,
  emptyLabel = 'Choose file…',
  browseLabel = 'Select file',
  onChange,
  className,
  'data-testid': testId,
}: IntegratedFileSelectProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  const openPicker = () => {
    if (!disabled) {
      inputRef.current?.click()
    }
  }

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e)
    e.target.value = ''
  }

  const trimmed = displayName.trim()
  const isPlaceholder = !trimmed
  const showText = trimmed || emptyLabel

  return (
    <div
      className={cn(
        'flex min-h-[2.5rem] overflow-hidden rounded-lg border border-slate-300 bg-white shadow-sm',
        className,
      )}
    >
      <button
        type="button"
        disabled={disabled}
        data-testid={testId ? `${testId}-choose` : undefined}
        aria-label={isPlaceholder ? emptyLabel : `Selected file: ${trimmed}`}
        onClick={openPicker}
        className={cn(
          'min-w-0 flex-1 truncate px-3 py-2 text-left text-sm transition',
          isPlaceholder ? 'text-slate-400' : 'text-slate-700',
          'hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-button/40 disabled:cursor-not-allowed disabled:opacity-60',
        )}
      >
        {showText}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        disabled={disabled}
        data-testid={testId ? `${testId}-input` : undefined}
        className="sr-only"
        onChange={handleInputChange}
      />
      <button
        type="button"
        disabled={disabled}
        data-testid={testId ? `${testId}-select-file` : undefined}
        onClick={openPicker}
        className={cn(
          'shrink-0 border-l border-slate-300 bg-button-soft px-4 py-2 text-sm font-semibold text-button transition',
          'hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-button/40 disabled:cursor-not-allowed disabled:opacity-60',
        )}
      >
        {browseLabel}
      </button>
    </div>
  )
}
